import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  clearRecentSearches,
  getRecentSearches,
  globalSearch,
} from "../../api/searchApi";

const GROUP_LABELS = {
  reports: "Reports",
  documents: "Documents",
  evidence: "Evidence",
  companies: "Companies",
  leads: "CRM Leads",
  notifications: "Notifications",
  auditLogs: "Audit Logs",
};

function escapeRegex(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Highlight({ text, query }) {
  const content = String(text || "");
  const search = String(query || "").trim();

  if (!search) return content;

  const parts = content.split(new RegExp(`(${escapeRegex(search)})`, "ig"));

  return parts.map((part, index) =>
    part.toLowerCase() === search.toLowerCase() ? (
      <mark key={`${part}-${index}`} style={styles.highlight}>
        {part}
      </mark>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
}

export default function GlobalSearch({ onNavigate }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const resultRefs = useRef([]);

  const groups = useMemo(() => {
    if (!results?.groups) return [];

    return Object.entries(results.groups).filter(
      ([, items]) => Array.isArray(items) && items.length > 0
    );
  }, [results]);

  const flatResults = useMemo(
    () => groups.flatMap(([, items]) => items),
    [groups]
  );

  async function loadRecentSearches() {
    try {
      const data = await getRecentSearches(8);
      setRecentSearches(Array.isArray(data?.searches) ? data.searches : []);
    } catch (_error) {
      setRecentSearches([]);
    }
  }

  useEffect(() => {
    loadRecentSearches();
  }, []);

  useEffect(() => {
    const onGlobalKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }

      if (event.key === "Escape") {
        setOpen(false);
        setActiveIndex(-1);
      }
    };

    const onPointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };

    window.addEventListener("keydown", onGlobalKeyDown);
    document.addEventListener("mousedown", onPointerDown);

    return () => {
      window.removeEventListener("keydown", onGlobalKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, []);

  useEffect(() => {
    const value = query.trim();

    if (value.length < 2) {
      setResults(null);
      setError("");
      setLoading(false);
      setActiveIndex(-1);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        setError("");
        setActiveIndex(-1);
        setResults(await globalSearch(value, 6, controller.signal));
        setOpen(true);
        loadRecentSearches();
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Search failed");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 280);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  useEffect(() => {
    if (activeIndex >= 0) {
      resultRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const selectResult = (item) => {
    setOpen(false);
    setActiveIndex(-1);

    if (typeof onNavigate === "function") {
      onNavigate(item.targetPage || "Dashboard", item);
    }
  };

  const runRecentSearch = (value) => {
    setQuery(value);
    setOpen(true);
    inputRef.current?.focus();
  };

  const handleInputKeyDown = (event) => {
    if (!open) return;

    if (event.key === "ArrowDown" && flatResults.length > 0) {
      event.preventDefault();
      setActiveIndex((current) =>
        current >= flatResults.length - 1 ? 0 : current + 1
      );
    }

    if (event.key === "ArrowUp" && flatResults.length > 0) {
      event.preventDefault();
      setActiveIndex((current) =>
        current <= 0 ? flatResults.length - 1 : current - 1
      );
    }

    if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      selectResult(flatResults[activeIndex]);
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const handleClearRecent = async () => {
    try {
      await clearRecentSearches();
      setRecentSearches([]);
    } catch (err) {
      setError(err.message || "Failed to clear recent searches");
    }
  };

  let flatIndex = -1;

  return (
    <div ref={wrapperRef} style={styles.wrapper}>
      <div style={styles.inputWrap}>
        <span style={styles.searchIcon}>⌕</span>
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleInputKeyDown}
          onFocus={() => setOpen(true)}
          placeholder="Search reports, evidence, documents, CRM..."
          aria-label="Global search"
          aria-expanded={open}
          aria-controls="ecovanta-global-search-results"
          style={styles.input}
        />
        {query && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setQuery("");
              setResults(null);
              setActiveIndex(-1);
              inputRef.current?.focus();
            }}
            style={styles.clearButton}
          >
            ×
          </button>
        )}
        <span style={styles.shortcut}>Ctrl K</span>
      </div>

      {open && (
        <div id="ecovanta-global-search-results" style={styles.panel}>
          <div style={styles.brandHeader}>
            <span style={styles.brandLogoFrame} aria-hidden="true">
              <img
                src="/logo/ecovanta-logo.png"
                alt=""
                style={styles.brandLogo}
              />
            </span>
            <div>
              <strong style={styles.brandTitle}>Ecovanta Search</strong>
              <div style={styles.brandSubtitle}>Search across your ESG workspace</div>
            </div>
          </div>
          {query.trim().length < 2 && (
            <div>
              <div style={styles.panelHeadingRow}>
                <strong style={styles.panelHeading}>Recent searches</strong>
                {recentSearches.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearRecent}
                    style={styles.textButton}
                  >
                    Clear
                  </button>
                )}
              </div>

              {recentSearches.length === 0 ? (
                <div style={styles.state}>
                  Type at least two characters to search across Ecovanta.
                </div>
              ) : (
                <div style={styles.recentList}>
                  {recentSearches.map((item) => (
                    <button
                      key={item._id || item.query}
                      type="button"
                      onClick={() => runRecentSearch(item.query)}
                      style={styles.recentItem}
                    >
                      <span>↻</span>
                      <span style={styles.recentQuery}>{item.query}</span>
                      <span style={styles.recentCount}>{item.resultCount || 0}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {query.trim().length >= 2 && loading && (
            <div style={styles.state}>Searching...</div>
          )}

          {query.trim().length >= 2 && !loading && error && (
            <div style={{ ...styles.state, color: "#991b1b" }}>{error}</div>
          )}

          {query.trim().length >= 2 &&
            !loading &&
            !error &&
            results?.total === 0 && (
              <div style={styles.state}>
                <strong>No results for “{query.trim()}”.</strong>
                <span style={styles.suggestion}>
                  Try a company name, ESRS code, document title, status, or lead.
                </span>
              </div>
            )}

          {query.trim().length >= 2 &&
            !loading &&
            !error &&
            groups.map(([groupName, items]) => (
              <section key={groupName} style={styles.group}>
                <div style={styles.groupTitle}>
                  <span>{GROUP_LABELS[groupName] || groupName}</span>
                  <span style={styles.groupCount}>
                    {results?.counts?.[groupName] ?? items.length}
                  </span>
                </div>

                {items.map((item) => {
                  flatIndex += 1;
                  const currentIndex = flatIndex;
                  const isActive = activeIndex === currentIndex;

                  return (
                    <button
                      key={`${item.type}-${item.id}`}
                      ref={(element) => {
                        resultRefs.current[currentIndex] = element;
                      }}
                      type="button"
                      onMouseEnter={() => setActiveIndex(currentIndex)}
                      onClick={() => selectResult(item)}
                      style={{
                        ...styles.result,
                        ...(isActive ? styles.activeResult : {}),
                      }}
                    >
                      <span style={styles.resultIcon}>{item.icon || "•"}</span>
                      <span style={styles.resultText}>
                        <strong style={styles.resultTitle}>
                          <Highlight text={item.title} query={query} />
                        </strong>
                        <span style={styles.resultSubtitle}>
                          <Highlight
                            text={item.subtitle || item.snippet || ""}
                            query={query}
                          />
                        </span>
                      </span>
                      {item.metadata?.confidence ? (
                        <span style={styles.meta}>
                          {item.metadata.confidence}%
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </section>
            ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: { position: "relative", width: "min(620px, 52vw)", minWidth: 260 },
  inputWrap: { display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 12, padding: "0 10px", boxShadow: "0 4px 16px rgba(15,23,42,.05)" },
  searchIcon: { color: "#64748b", fontSize: 20 },
  input: { flex: 1, minWidth: 0, border: 0, outline: 0, padding: "10px 4px", fontSize: 14, color: "#0f172a", background: "transparent" },
  clearButton: { border: 0, background: "transparent", color: "#64748b", cursor: "pointer", fontSize: 18, lineHeight: 1 },
  shortcut: { border: "1px solid #e2e8f0", borderRadius: 6, padding: "2px 6px", color: "#64748b", fontSize: 10, whiteSpace: "nowrap" },
  panel: { position: "absolute", zIndex: 1000, top: "calc(100% + 8px)", left: 0, right: 0, maxHeight: "70vh", overflowY: "auto", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, boxShadow: "0 24px 60px rgba(15,23,42,.18)", padding: 8 },
  brandHeader: { display: "flex", alignItems: "center", gap: 10, padding: "8px 10px 10px", borderBottom: "1px solid #f1f5f9", marginBottom: 2 },
  brandLogoFrame: { width: 34, height: 34, display: "grid", placeItems: "center", overflow: "hidden", borderRadius: 9, background: "#fff", flexShrink: 0 },
  brandLogo: { width: 52, height: 52, maxWidth: "none", objectFit: "contain", display: "block" },
  brandTitle: { display: "block", color: "#0f172a", fontSize: 13, lineHeight: 1.2 },
  brandSubtitle: { marginTop: 2, color: "#64748b", fontSize: 10 },
  panelHeadingRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px 4px" },
  panelHeading: { color: "#334155", fontSize: 12 },
  textButton: { border: 0, background: "transparent", color: "#0f766e", fontSize: 11, fontWeight: 800, cursor: "pointer" },
  state: { padding: 18, textAlign: "center", color: "#64748b", fontSize: 14, display: "grid", gap: 6 },
  suggestion: { fontSize: 12 },
  recentList: { display: "grid", gap: 3, paddingTop: 5 },
  recentItem: { width: "100%", display: "flex", alignItems: "center", gap: 9, border: 0, background: "transparent", borderRadius: 9, padding: "9px 10px", textAlign: "left", cursor: "pointer", color: "#334155" },
  recentQuery: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  recentCount: { color: "#94a3b8", fontSize: 11, fontWeight: 700 },
  group: { padding: "5px 0" },
  groupTitle: { padding: "7px 10px", color: "#64748b", fontSize: 10, fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase", display: "flex", justifyContent: "space-between", alignItems: "center" },
  groupCount: { display: "inline-grid", placeItems: "center", minWidth: 20, height: 20, padding: "0 5px", borderRadius: 999, background: "#f1f5f9", color: "#475569", fontSize: 10, letterSpacing: 0 },
  result: { width: "100%", display: "flex", alignItems: "center", gap: 10, border: "1px solid transparent", background: "transparent", borderRadius: 10, padding: "9px 10px", textAlign: "left", cursor: "pointer" },
  activeResult: { background: "#ecfdf5", borderColor: "#a7f3d0" },
  resultIcon: { width: 24, flexShrink: 0, textAlign: "center" },
  resultText: { minWidth: 0, flex: 1, display: "grid", gap: 2 },
  resultTitle: { color: "#0f172a", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  resultSubtitle: { color: "#64748b", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  meta: { color: "#0f766e", fontSize: 11, fontWeight: 800 },
  highlight: { background: "#fef3c7", color: "inherit", borderRadius: 3, padding: 0 },
};
